import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { httpPostGraphql } from "shared/api/httpClient";
import { subdomain } from "shared/stores/oidc";
import { maybe } from "shared/utils/maybe";
import { qk } from "shared/utils/qk";
import { zDate, zInstant } from "shared/utils/zod";
import { graphql } from "src/gql";

export function useCustomer() {
    return useSuspenseQuery({
        queryKey: qk("customer", "id-subdomain"),
        select: ({ customer }) => customer,
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query CurrentCustomer($subdomain: String!) {
                        customer(subDomain: $subdomain) {
                            id
                            subdomain
                            name
                            primaryColor
                            languages
                            blackoutPeriod {
                                start
                                end
                            }
                        }
                    }
                `),
                { subdomain },
            ),
    });
}

export function useIsInBlackoutPeriod(dateLike: Date | string | number | null | undefined) {
    const time = zDate.safeParse(dateLike).data;
    const { data, ...rest } = useQuery({
        queryKey: qk("customer", "blackout-period-contains", time?.toString() ?? "unset"),
        queryFn: () => maybe(time)?.take(isInBlackoutPeriod) ?? false,
    });

    return { isInBlackoutPeriod: data ?? false, ...rest };
}

async function isInBlackoutPeriod(time: Date): Promise<boolean> {
    const sample = zInstant.safeParse(time).data;
    if (!sample) return false;

    const response = await httpPostGraphql(
        graphql(`
            query IsInBlackoutPeriod($sample: Instant!) {
                customer {
                    blackoutPeriod {
                        contains(time: $sample)
                    }
                }
            }
        `),
        { sample },
    );

    return response.customer.blackoutPeriod?.contains ?? false;
}
